import React, { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  Home,
  BarChart2,
  Bookmark,
  TreePine,
  FileText,
  User,
  HelpCircle,
} from "lucide-react";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { useUserSubscription } from "@/utils/subscriptionUtils";
import NavigationItem from "@/components/ui/NavigationItem";

interface SidebarProps {
  userStats?: {
    regionsVisited: number;
    reportsCount: number;
    reportsRemaining: number;
  };
  savedRegions?: string[];
  savedIcebergsCount?: number;
  userProjects?: Array<{ id: string; name: string; type?: string }>;
  className?: string;
  onNavigate?: (path: string) => void; // Custom navigation handler for auth requirements
}

const Sidebar: React.FC<SidebarProps> = ({
  userStats = { regionsVisited: 0, reportsCount: 0, reportsRemaining: 10 },
  savedRegions = [],
  savedIcebergsCount = 0,
  userProjects = [],
  className = "",
  onNavigate,
}) => {
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const hoverTimer = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSupabaseAuth();
  const { tier } = useUserSubscription();

  // Navigation active state
  const isActivePage = (path: string) => location.pathname === path;

  const toggleNavbar = () => {
    setIsNavbarOpen(!isNavbarOpen);
  };

  const handleNavigation = (path: string) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  return (
    <aside
      className={`${
        isNavbarOpen ? "w-64" : "w-16"
      } sticky top-0 h-screen bg-white/80 dark:bg-[#262626] backdrop-blur-xl border-r border-border/50 mr-4 transition-all duration-300 ease-out flex flex-col shadow-sm border-r-4 dark:border-r-[#fff]/20 border-r-grey/20 border-r-dotted ${className}`}
      onMouseEnter={() => {
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
        hoverTimer.current = setTimeout(() => setIsNavbarOpen(true), 150);
      }}
      onMouseLeave={() => {
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
        hoverTimer.current = setTimeout(() => setIsNavbarOpen(false), 300);
      }}
    >
      {/* Header with Logo */}
      <div className="flex items-center justify-between px-3 py-4 ">
        <div
          className="flex items-center gap-2 transition-all duration-200"
          onClick={() => navigate("/")}
          style={{ cursor: "pointer" }}
        >
          <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">M</span>
          </div>
          <span
            className={`font-semibold text-foreground transition-all duration-200 ${
              isNavbarOpen ? "opacity-100" : "opacity-0 w-0"
            }`}
          >
            Myǃhū
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {/* Primary Navigation */}
        <div className="space-y-1">
          {/* Home */}
          <NavigationItem
            icon={<Home className="w-4 h-4" strokeWidth={2} />}
            label="Home"
            isActive={isActivePage("/")}
            onClick={() => handleNavigation("/")}
            isExpanded={isNavbarOpen}
          />

          {/* Dashboard */}
          <NavigationItem
            icon={<BarChart2 className="w-4 h-4" />}
            label="Dashboard"
            isActive={isActivePage("/dashboard")}
            onClick={() => handleNavigation("/dashboard")}
            isExpanded={isNavbarOpen}
            badge={user && userStats.reportsRemaining <= 2 ? "!" : undefined}
          />

          {/* Bookmarks */}
          <NavigationItem
            icon={<Bookmark className="w-4 h-4" />}
            label="Bookmarks"
            isActive={isActivePage("/my-regions")}
            onClick={() => handleNavigation("/my-regions")}
            isExpanded={isNavbarOpen}
            badge={
              (savedRegions?.length || 0) + savedIcebergsCount > 0
                ? ((savedRegions?.length || 0) + savedIcebergsCount).toString()
                : undefined
            }
          />

          {/* Projects */}
          <NavigationItem
            icon={<TreePine className="w-4 h-4" />}
            label="Projects"
            isActive={isActivePage("/projects")}
            onClick={() => handleNavigation("/projects")}
            isExpanded={isNavbarOpen}
            badge={
              userProjects?.length > 0
                ? userProjects.length.toString()
                : undefined
            }
          />

          {/* Reports */}
          <NavigationItem
            icon={<FileText className="w-4 h-4" />}
            label="Reports"
            isActive={isActivePage("/my-reports")}
            onClick={() => handleNavigation("/my-reports")}
            isExpanded={isNavbarOpen}
            badge={
              userStats.reportsCount > 0
                ? userStats.reportsCount.toString()
                : undefined
            }
          />
        </div>

        {/* Separator */}
        <div className="py-2 pb-6">
          <div className="border-t border-border/30 dark:border-[#fff]/20 mb-4 mt-6"></div>
        </div>

        {/* Secondary Navigation */}
        <div className="space-y-1">
          {/* Knowledge Hub navigation temporarily hidden
          <NavigationItem
            icon={<FontAwesomeIcon icon={faGraduationCap} className="w-4 h-4" />}
            label="Knowledge Hub"
            isActive={isActivePage('/knowledge-hub')}
            onClick={() => handleNavigation('/knowledge-hub')}
            isExpanded={isNavbarOpen}
          />
          */}
          <NavigationItem
            icon={<User className="w-4 h-4" />}
            label="My Account"
            isActive={isActivePage("/profile")}
            onClick={() => handleNavigation("/profile")}
            isExpanded={isNavbarOpen}
          />

          {/* Help */}
          <NavigationItem
            icon={<HelpCircle className="w-4 h-4" />}
            label="Help"
            isActive={isActivePage("/help")}
            onClick={() => handleNavigation("/help")}
            isExpanded={isNavbarOpen}
          />
        </div>
      </nav>
    </aside>
  );
};

export default Sidebar;
